import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { sendsms, smstemplates } from "../../../services/sms";
import { listBranches } from "../../../services/admin";

/**
 * Communication > Send SMS. Pick an audience (all / members / employees),
 * optionally scope it to a branch, type a message and send. Delivery is queued
 * on the server; this screen only composes the broadcast.
 */
export default function SendSms() {
    const navigate = useNavigate();
    const [sentTo, setSentTo] = useState("members");
    const [branchIds, setBranchIds] = useState([]);
    const [templateId, setTemplateId] = useState("");
    const [message, setMessage] = useState("");
    const [branches, setBranches] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [sending, setSending] = useState(false);

    useEffect(() => {
        // Only active branches can be targeted.
        listBranches().then((res) => {
            if (res?.status === true && Array.isArray(res.data)) {
                const active = res.data.filter((b) => Number(b.active) === 1);
                setBranches(
                    active.map((b) => ({
                        value: b.id,
                        label: b.branch_name || b.name,
                    }))
                );
            }
        });

        // SMS templates to pick from (text SMS templates only).
        smstemplates().then((res) => {
            const list = res?.data?.data || [];
            setTemplates(
                list.filter((t) => ["sms", "both"].includes(t.notification_type))
            );
        });
    }, []);

    function handleTemplate(id) {
        setTemplateId(id);
        const tpl = templates.find((t) => String(t.id) === String(id));
        if (tpl) {
            setMessage(tpl.sms_body || "");
        }
    }

    function handlesubmit(e) {
        e.preventDefault();

        if (!templateId) {
            toast.error("Select a template.");
            return;
        }
        if (!message.trim()) {
            toast.error("Enter a message.");
            return;
        }

        const temp = new FormData();
        temp.append("sent_to", sentTo);
        temp.append("template_id", templateId);
        // Empty = all branches.
        temp.append("branch_ids", branchIds.map((b) => b.value).join(","));
        temp.append("message", message.trim());

        setSending(true);
        sendsms(temp)
            .then((res) => {
                if (res?.status === true) {
                    toast.success(res.message);
                    setMessage("");
                    navigate("/sms/list/");
                } else if (res?.message) {
                    toast.error(res.message);
                }
            })
            .finally(() => setSending(false));
    }

    return (
        <div>
            <div className="page-header">
                <h2 className="main-content-title tx-24 mg-b-5">Send SMS</h2>
                <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                        <a>Communication</a>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                        Send SMS
                    </li>
                </ol>
            </div>

            <div className="content-body">
                <div className="row">
                    <div className="col-xl-12">
                        <div className="card">
                            <div className="card-header">
                                <h2 className="main-content-title tx-24 mg-b-5">
                                    Compose Message
                                </h2>
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-md-12">
                                        <div className="mb-3">
                                            <label className="form-label">
                                                Template <span className="text-danger">*</span>
                                            </label>
                                            <select
                                                className="form-control"
                                                value={templateId}
                                                onChange={(e) => handleTemplate(e.target.value)}
                                            >
                                                <option value="">-- Select Template --</option>
                                                {templates.map((t) => (
                                                    <option key={t.id} value={t.id}>
                                                        {t.template_name}
                                                    </option>
                                                ))}
                                            </select>
                                            {/* <small className="text-muted">
                                                Choosing a template fills the message below; you can still edit it.
                                            </small> */}
                                        </div>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="mb-3">
                                            <label className="form-label">
                                                Send To <span className="text-danger">*</span>
                                            </label>
                                            <select
                                                className="form-control"
                                                value={sentTo}
                                                onChange={(e) => setSentTo(e.target.value)}
                                            >
                                                {/* <option value="all">All (Members &amp; Employees)</option> */}
                                                <option value="members">Members</option>
                                                <option value="employees">Employees</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="mb-3">
                                            <label className="form-label">Branch (Leave empty to send to all branches.)</label>
                                            <Select
                                                isMulti
                                                options={branches}
                                                value={branchIds}
                                                onChange={(vals) => setBranchIds(vals || [])}
                                                placeholder="All Branches"
                                                closeMenuOnSelect={false}
                                            />
                                            {/* <small className="text-muted">
                                                Leave empty to send to all branches.
                                            </small> */}
                                        </div>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-md-12">
                                        <div className="mb-3">
                                            <label className="form-label">
                                                Message <span className="text-danger">*</span>
                                            </label>
                                            <textarea
                                                className="form-control"
                                                rows="4"
                                                placeholder="Enter Message"
                                                value={message}
                                                onChange={(e) => setMessage(e.target.value)}
                                            />
                                            <small className="text-muted">
                                                {message.length} characters
                                            </small>
                                        </div>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-md-12 text-end">
                                        <a
                                            type="submit"
                                            className={`btn btn-success ${sending ? "disabled" : ""}`}
                                            onClick={sending ? undefined : handlesubmit}
                                        >
                                            {sending ? "Sending..." : "Send SMS"}
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
